import { useState, useEffect, KeyboardEvent } from "react";

import { useToken } from "@chakra-ui/react";

import { validateUrlWithMessage } from "../utils/validateUrl";

export interface LinkDialogProps {
  open: boolean;
  onSubmit: (url: string) => void;
  onCancel: () => void;
  initialUrl: string;
  onRemove?: () => void;
  labels?: {
    insertTitle?: string;
    editTitle?: string;
    helpText?: string;
    placeholder?: string;
    insertButton?: string;
    updateButton?: string;
    removeButton?: string;
    cancelButton?: string;
  };
  errorMessages?: {
    errorUrlEmpty?: string;
    errorUrlProtocol?: string;
    errorUrlInvalid?: string;
  };
}

export const LinkDialog = ({
  open,
  initialUrl,
  onSubmit,
  onCancel,
  onRemove,
  labels = {},
  errorMessages
}: LinkDialogProps): JSX.Element | null => {
  const [url, setUrl] = useState(initialUrl);
  const [error, setError] = useState("");
  const [white, gray700, gray500, gray300, gray200, red600, blue500] = useToken(
    "colors",
    [
      "white",
      "gray.700",
      "gray.500",
      "gray.300",
      "gray.200",
      "red.600",
      "blue.500"
    ]
  );
  const [radiiLg, radiiMd] = useToken("radii", ["lg", "md"]);
  const [shadowXl] = useToken("shadows", ["xl"]);
  const [fontSizeLg, fontSizeXs, fontSizeSm] = useToken("fontSizes", [
    "lg",
    "xs",
    "sm"
  ]);

  const {
    insertTitle,
    editTitle,
    helpText,
    placeholder,
    insertButton,
    updateButton,
    removeButton,
    cancelButton
  } = labels;

  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setError("");
    }
  }, [initialUrl, open]);

  if (!open) return null;

  const handleSubmit = () => {
    const validationError = validateUrlWithMessage(url, errorMessages);
    if (validationError) {
      setError(validationError);
      return;
    }
    onSubmit(url.trim());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: white,
          borderRadius: radiiLg,
          padding: "24px",
          minWidth: "400px",
          maxWidth: "90%",
          boxShadow: shadowXl
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            fontSize: fontSizeLg,
            fontWeight: 600,
            color: gray700,
            margin: "0 0 8px 0"
          }}
        >
          {initialUrl ? editTitle : insertTitle}
        </h3>

        <p
          style={{
            fontSize: fontSizeXs,
            color: gray500,
            margin: "0 0 12px 0"
          }}
        >
          {helpText}
        </p>

        <input
          type="text"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError("");
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "10px 12px",
            border: error ? `2px solid ${red600}` : `1px solid ${gray300}`,
            borderRadius: radiiMd,
            fontSize: fontSizeSm,
            outline: "none",
            boxSizing: "border-box"
          }}
          autoFocus
          aria-invalid={!!error}
        />

        {error && (
          <div
            style={{
              color: red600,
              fontSize: fontSizeXs,
              marginTop: "4px"
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "16px",
            justifyContent: "flex-end"
          }}
        >
          {initialUrl && onRemove && (
            <button
              type="button"
              onClick={onRemove}
              style={{
                padding: "8px 16px",
                backgroundColor: red600,
                color: white,
                border: "none",
                borderRadius: radiiMd,
                cursor: "pointer",
                fontSize: fontSizeSm,
                marginRight: "auto"
              }}
            >
              {removeButton}
            </button>
          )}

          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "8px 16px",
              backgroundColor: gray200,
              color: gray700,
              border: "none",
              borderRadius: radiiMd,
              cursor: "pointer",
              fontSize: fontSizeSm
            }}
          >
            {cancelButton}
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            style={{
              padding: "8px 16px",
              backgroundColor: blue500,
              color: white,
              border: "none",
              borderRadius: radiiMd,
              cursor: "pointer",
              fontSize: fontSizeSm
            }}
          >
            {initialUrl ? updateButton : insertButton}
          </button>
        </div>
      </div>
    </div>
  );
};
